// src/shared/email-schedule.js - which day, and how often, each movable weekly email goes (v133.56).
//
// Matt, 6 Oct 2026: there are too many Monday reports (the owner gets most of them), so each weekly
// email's day and frequency can be set in Admin, Comms, Automated, Emails. Stored in app_config under
// '<email>_schedule' (no migration). An absent or broken setting means exactly today's behaviour.
//
// Pure: imported by the Worker (the cron decides "is it due today?") and the admin page (the labels).
//
// NOT movable, deliberately (they keep their fixed day and show it on their card):
//   training_approval  Monday 05:30: the same switch publishes the week's session
//   cs_weekly          Monday 10:30: ELC Jack writes the review at 06:00 that Monday
//   svr_monthly, reviews_outstanding  monthly, about the month just ended
//   review_chase       Mondays in the run-up to quarter end
//   daily emails       daily by nature

export const FREQS = ['weekly', 'fortnightly', 'monthly'];
export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// covers: what each send reports on, so a fortnightly or monthly choice is honest about it.
export const SCHEDULABLE = {
  am_briefing:        { dow: 1, covers: 'the last full week' },
  google_reviews:     { dow: 1, covers: 'the last 7 days' },
  customer_requests:  { dow: 1, covers: 'the last full week' },
  jack_digest:        { dow: 1, covers: 'the last 7 days' },
  chat_digest:        { dow: 1, covers: 'the last 7 days' },
  induction_progress: { dow: 1, covers: null },   // where things stand on the day: any frequency fits
  holiday_status:     { dow: 1, covers: null },
  weekly_email:       { dow: 3, covers: 'the last full week' },
};

export function scheduleKey(id) { return id + '_schedule'; }

export function defaultSchedule(id) {
  const s = SCHEDULABLE[id];
  return s ? { freq: 'weekly', dow: s.dow } : null;
}

// Any stored value (object or JSON string) -> a clean { freq, dow }, falling back to the default.
export function parseSchedule(id, value) {
  const def = defaultSchedule(id);
  if (!def) return null;
  let v = value;
  if (typeof v === 'string') { try { v = JSON.parse(v); } catch (e) { return def; } }
  if (!v || typeof v !== 'object') return def;
  const freq = FREQS.indexOf(v.freq) >= 0 ? v.freq : def.freq;
  const dow = Number.isInteger(v.dow) && v.dow >= 0 && v.dow <= 6 ? v.dow : def.dow;
  return { freq, dow };
}

// Checks a schedule sent from the admin page. Returns { error } or { schedule }.
export function checkSchedule(id, input) {
  if (!SCHEDULABLE[id]) return { error: 'This email keeps a fixed day.' };
  const b = input || {};
  if (FREQS.indexOf(b.freq) < 0) return { error: 'Choose weekly, fortnightly or monthly.' };
  const dow = Number(b.dow);
  if (!Number.isInteger(dow) || dow < 0 || dow > 6) return { error: 'Choose a day.' };
  return { schedule: { freq: b.freq, dow } };
}

// Fortnights count from a fixed Monday, so "every other week" is stable and the same for every email.
const ANCHOR_MS = Date.parse('2026-01-05T00:00:00Z');   // a Monday

// Is it due on this Dubai date (YYYY-MM-DD)?
//   weekly       that weekday
//   fortnightly  that weekday, every other week (counted from the anchor)
//   monthly      the FIRST of that weekday in the month
export function isDueOn(schedule, ymd) {
  if (!schedule) return false;
  const ms = Date.parse(String(ymd).slice(0, 10) + 'T00:00:00Z');
  if (isNaN(ms)) return false;
  const d = new Date(ms);
  if (d.getUTCDay() !== schedule.dow) return false;
  if (schedule.freq === 'weekly') return true;
  if (schedule.freq === 'monthly') return d.getUTCDate() <= 7;
  if (schedule.freq === 'fortnightly') {
    const weeks = Math.floor((ms - ANCHOR_MS) / (7 * 86400000));
    return ((weeks % 2) + 2) % 2 === 0;
  }
  return false;
}

export function describeSchedule(schedule) {
  if (!schedule) return '';
  const day = DAY_NAMES[schedule.dow] || '';
  if (schedule.freq === 'fortnightly') return 'Every other ' + day;
  if (schedule.freq === 'monthly') return 'The first ' + day + ' of the month';
  return 'Every ' + day;
}

// The note under a fortnightly or monthly choice, when the email reports a fixed week.
export function coversNote(id, schedule) {
  const s = SCHEDULABLE[id];
  if (!s || !s.covers || !schedule || schedule.freq === 'weekly') return '';
  return 'Each send reports ' + s.covers + ', so the weeks in between are not in an email (they are still in the app).';
}
